import type { MarketKind, SideView } from "@screener/protocol";

/**
 * Модель декоративных карточек правой колонки входа и регистрации
 * (stage9-auth-redesign, Д5, handoff «Auth.dc.html»). Данные фиктивные: к
 * потоку демона карточки не подключены. Здесь — чистая часть без React и без
 * DOM, чтобы тик, форматы и запуск таймера проверялись без браузера.
 */

export interface DemoCard {
  ticker: string;
  exchange: string;
  market: MarketKind;
  /** Опорная цена, от которой считаются слоты. */
  price: number;
  /** Шаг цены: цена слота округляется до него. */
  step: number;
  /** Знаков после запятой в цене на экране. */
  decimals: number;
}

export const DEMO_CARDS: readonly DemoCard[] = [
  {
    ticker: "BTCUSDT",
    exchange: "binance",
    market: "perp",
    price: 83150,
    step: 50,
    decimals: 1,
  },
  {
    ticker: "ETH/USDT",
    exchange: "bybit",
    market: "spot",
    price: 2507.4,
    step: 0.5,
    decimals: 2,
  },
  {
    ticker: "SOLUSDT",
    exchange: "okx",
    market: "perp",
    price: 106.9,
    step: 0.05,
    decimals: 2,
  },
  {
    ticker: "XRPUSDT",
    exchange: "bitget",
    market: "perp",
    price: 2.4135,
    step: 0.0005,
    decimals: 4,
  },
  {
    ticker: "DOGE/USDT",
    exchange: "kucoin",
    market: "spot",
    price: 0.17925,
    step: 0.00005,
    decimals: 5,
  },
  {
    ticker: "SUIUSDT",
    exchange: "mexc",
    market: "perp",
    price: 3.4125,
    step: 0.001,
    decimals: 3,
  },
];

/**
 * Делит карточки на колонки подряд идущими кусками по `ceil(n / count)`:
 * первая колонка — первые карточки, вторая — следующие.
 */
export function splitDemoColumns<T>(cards: readonly T[], columnCount: number): T[][] {
  const size = Math.ceil(cards.length / columnCount);
  return Array.from({ length: columnCount }, (_, index) =>
    cards.slice(index * size, (index + 1) * size),
  );
}

/** Дистанция шести слотов от цены, %: три аска сверху, три бида снизу. */
export const SLOT_OFFSETS_PCT: readonly number[] = [0.38, 0.22, 0.08, -0.05, -0.17, -0.31];

/** Сколько слотов в карточке стоят над разделителем (аски). */
export const ASK_SLOT_COUNT = 3;

/** Меньше стольких включённых строк в карточке не остаётся: слот не гасится. */
export const MIN_ACTIVE_SLOTS = 2;

/** Вероятность стартового включения слота. */
const INITIAL_ON_PROBABILITY = 0.65;
/** Вероятность, что за тик переключится хотя бы один слот. */
const ONE_FLIP_PROBABILITY = 0.6;
/** Если один слот не выпал, вероятность переключить два. */
const TWO_FLIPS_PROBABILITY = 0.5;

export interface Slot {
  on: boolean;
  /** Объём фиктивной плотности, USD. */
  usd: number;
  /** Возраст, секунды. */
  age: number;
}

export type CardSlots = readonly Slot[];
export type AllSlots = readonly CardSlots[];

/** Источник случайности: `Math.random` в бою, подмена в тестах. */
export type Random = () => number;

export function slotSide(index: number): SideView {
  return index < ASK_SLOT_COUNT ? "ask" : "bid";
}

/** 60K–2.26M, смещён к малым значениям, шаг 100. */
export function randomUsd(random: Random): number {
  return Math.round((60_000 + random() ** 2 * 2_200_000) / 100) * 100;
}

/**
 * Начальный кадр. Слот включён с вероятностью 0.65; если в карточке включено
 * меньше двух, добираются первые выключенные: пустая карточка в неподвижном
 * кадре («уменьшить движение») была бы пустым прямоугольником.
 */
export function createSlots(cards: readonly DemoCard[], random: Random): AllSlots {
  return cards.map(() => {
    const slots: Slot[] = SLOT_OFFSETS_PCT.map(() => ({
      on: random() < INITIAL_ON_PROBABILITY,
      usd: randomUsd(random),
      age: Math.round(20 + random() * 900),
    }));
    for (const slot of slots) {
      if (slots.filter((item) => item.on).length >= MIN_ACTIVE_SLOTS) {
        break;
      }
      slot.on = true;
    }
    return slots;
  });
}

/**
 * Один тик: возраст всех строк +1 с; с вероятностью 0.6 переключается один
 * слот, иначе с вероятностью 0.5 — два (случайные карточка и слот). Включённый
 * слот получает новый объём и возраст 0. Слот не гасится, если в карточке
 * останется меньше двух включённых.
 */
export function tickSlots(slots: AllSlots, random: Random): AllSlots {
  const next: Slot[][] = slots.map((card) => card.map((slot) => ({ ...slot, age: slot.age + 1 })));
  const flips = random() < ONE_FLIP_PROBABILITY ? 1 : random() < TWO_FLIPS_PROBABILITY ? 2 : 0;
  for (let step = 0; step < flips; step += 1) {
    const cardIndex = Math.floor(random() * next.length);
    const card = next[cardIndex];
    if (card === undefined) {
      continue;
    }
    const slotIndex = Math.floor(random() * card.length);
    const slot = card[slotIndex];
    if (slot === undefined) {
      continue;
    }
    const activeCount = card.filter((item) => item.on).length;
    if (slot.on && activeCount <= MIN_ACTIVE_SLOTS) {
      continue;
    }
    card[slotIndex] = slot.on
      ? { ...slot, on: false }
      : { on: true, usd: randomUsd(random), age: 0 };
  }
  return next;
}

/** Цена слота: `round(цена · (1 + d/100) / шаг) · шаг`, с нужным числом знаков. */
export function slotPrice(card: DemoCard, offsetPct: number): string {
  const price = Math.round((card.price * (1 + offsetPct / 100)) / card.step) * card.step;
  return price.toFixed(card.decimals);
}

/** Дистанция в процентах, без знака: `0.22%`. */
export function formatDistancePct(offsetPct: number): string {
  return `${Math.abs(offsetPct).toFixed(2)}%`;
}

/** Объём коротко: `1.2M`, `640K`. */
export function formatDemoUsd(value: number): string {
  if (value >= 1e6) {
    return `${(value / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  }
  return `${Math.round(value / 1e3)}K`;
}

/** Отступ между соседними видимыми строками карточки, px (`gap-y-0.5` настоящей). */
export const ROW_GAP_PX = 2;

/**
 * Верхний отступ каждой строки, px. Зазор сетки не годится: скрытая строка
 * высотой 0 всё равно оставила бы свой зазор (отрицательный margin дорожку
 * ниже 0 не сжимает), поэтому ритм ведут отступы. Скрытая и первая видимая
 * строки — 0, остальные видимые — `ROW_GAP_PX`.
 */
export function rowMarginsPx(card: CardSlots): number[] {
  let seenVisible = false;
  return card.map((slot) => {
    if (!slot.on) {
      return 0;
    }
    const margin = seenVisible ? ROW_GAP_PX : 0;
    seenVisible = true;
    return margin;
  });
}

/** Индекс последней включённой строки; -1, если включённых нет. */
export function lastActiveIndex(card: CardSlots): number {
  return card.reduce((last, slot, index) => (slot.on ? index : last), -1);
}

/** Наибольший объём среди включённых слотов карточки; 1, если включённых нет. */
export function maxActiveUsd(card: CardSlots): number {
  return Math.max(1, ...card.filter((slot) => slot.on).map((slot) => slot.usd));
}

/** Запрос «уменьшить движение» (`prefers-reduced-motion`). */
export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** Запрос широкого экрана: с брейкпоинта `md` (768 px) правая колонка видна. */
export const WIDE_SCREEN_QUERY = "(min-width: 768px)";

/** Запрос экрана xl: с него видна вторая колонка карточек. */
export const XL_SCREEN_QUERY = "(min-width: 1280px)";

/** Параметры колонки: сдвиг вниз, интервал тика и запрос ширины, при которой она видна. */
export interface DemoColumnParams {
  offsetPx: number;
  intervalMs: number;
  query: string;
}

export const DEMO_COLUMNS: readonly DemoColumnParams[] = [
  { offsetPx: 0, intervalMs: 1000, query: WIDE_SCREEN_QUERY },
  { offsetPx: 48, intervalMs: 1300, query: XL_SCREEN_QUERY },
];

/** Когда и как часто тикает колонка. */
export interface TickerOptions {
  /** Запрос ширины, при которой колонка видна: скрытая не тикает. */
  query: string;
  intervalMs: number;
}

/** Минимум от `MediaQueryList`, нужный таймеру. */
export interface MediaQuery {
  matches: boolean;
  addEventListener: (type: "change", listener: () => void) => void;
  removeEventListener: (type: "change", listener: () => void) => void;
}

export interface TickerDeps {
  matchMedia: (query: string) => MediaQuery;
  setInterval: (handler: () => void, ms: number) => number;
  clearInterval: (id: number) => void;
}

/**
 * Запуск таймера колонки карточек. Тикает только когда колонка видна
 * (запрос ширины из параметров) и без «уменьшить движение»; при смене любой из настроек таймер
 * заводится или снимается. В остальное время остаётся неподвижный кадр.
 * Возвращает очистку для `useEffect`: снимает таймер и слушатели.
 * Окружение — параметр: проверяется без браузера.
 */
export function startCardsTicker(
  onTick: () => void,
  options: TickerOptions,
  deps: TickerDeps = {
    matchMedia: (query) => window.matchMedia(query),
    setInterval: (handler, ms) => window.setInterval(handler, ms),
    clearInterval: (id) => window.clearInterval(id),
  },
): () => void {
  const wide = deps.matchMedia(options.query);
  const reduced = deps.matchMedia(REDUCED_MOTION_QUERY);
  let timer: number | null = null;

  const stop = () => {
    if (timer !== null) {
      deps.clearInterval(timer);
      timer = null;
    }
  };
  const sync = () => {
    const shouldRun = wide.matches && !reduced.matches;
    if (shouldRun && timer === null) {
      timer = deps.setInterval(onTick, options.intervalMs);
    } else if (!shouldRun) {
      stop();
    }
  };

  wide.addEventListener("change", sync);
  reduced.addEventListener("change", sync);
  sync();
  return () => {
    wide.removeEventListener("change", sync);
    reduced.removeEventListener("change", sync);
    stop();
  };
}
